import { features, OAuth, profilesApi, useThunkDispatch } from '@tunasong/redux'
import { Button, TextField } from '@tunasong/ui-lib'
import type { ChangeEvent, FC, FormEvent } from 'react'
import { useId, useRef, useState } from 'react'
import styles from './profile-name-step.module.css'

const maxNameLength = 80

export interface ProfileNameStepProps {
  profileId: string
  /** Called once the server has saved the name */
  onSaved: () => void
}

/**
 * The required name step: one field, Continue, and a quiet Sign out. There is no skip. Copy and behaviour are from
 * docs/ux/2026-10-17-profile-name-required-ux.md.
 */
export const ProfileNameStep: FC<ProfileNameStepProps> = props => {
  const { profileId, onSaved } = props
  const dispatch = useThunkDispatch()
  const [updateProfile] = profilesApi.useUpdateProfileMutation()
  const [value, setValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [hasError, setHasError] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const helperId = useId()
  const errorId = useId()

  const name = value.trim()
  const canContinue = name.length > 0 && !isSaving

  /** Leading whitespace is never part of a name, so it does not count against the cap */
  const handleChange = (ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setValue(ev.target.value.trimStart())

  const handleSubmit = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault()
    if (!canContinue) {
      return
    }
    setIsSaving(true)
    setHasError(false)
    try {
      await updateProfile({ id: profileId, profile: { name } }).unwrap()
      onSaved()
    } catch {
      setHasError(true)
      setIsSaving(false)
      inputRef.current?.focus()
    }
  }

  const handleSignOut = () => {
    void OAuth.logout()
    dispatch(features.user.actions.signout())
  }

  return (
    <main className={styles.page}>
      <form className={styles.card} onSubmit={handleSubmit} noValidate>
        <h1 className={styles.heading}>What should bandmates call you?</h1>
        <TextField
          ref={inputRef}
          className={styles.field}
          label="Your name"
          name="name"
          value={value}
          onChange={handleChange}
          autoComplete="name"
          autoFocus
          maxLength={maxNameLength}
          /** The helper and the error are both linked here; TextField's own helperText would be overridden */
          aria-describedby={hasError ? `${helperId} ${errorId}` : helperId}
          aria-invalid={hasError ? true : undefined}
          fullWidth
        />
        <p id={helperId} className={styles.helper}>
          Shown on comments, shares and setlists. You can change it later in Settings.
        </p>
        {hasError ? (
          <p id={errorId} role="alert" className={styles.error}>
            Couldn&apos;t save your name. Check your connection and try again.
          </p>
        ) : null}
        <Button type="submit" variant="contained" fullWidth disabled={!canContinue}>
          {isSaving ? 'Saving…' : 'Continue'}
        </Button>
      </form>
      <Button className={styles.signOut} variant="text" onClick={handleSignOut}>
        Sign out
      </Button>
    </main>
  )
}
